import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'

import styled from 'styled-components'

import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import MoreVertIcon from '@material-ui/icons/MoreVert'

import Collapse from '@material-ui/core/Collapse'

import List from '@eig-builder/core-ui/List'
import ListItem from '@eig-builder/core-ui/ListItem'
import ListItemText from '@eig-builder/core-ui/ListItemText'
import IconButton from '@eig-builder/core-ui/IconButton'
import Popover from '@eig-builder/core-ui/Popover'

import { DataElementLocations } from '@eig-builder/core-utils/helpers/tagging-helper'
import usePutAsJson from '@eig-builder/core-utils/hooks/usePutAsJson'
import usePostAsJson from '@eig-builder/core-utils/hooks/usePostAsJson'
import { useModalContext } from '@eig-builder/module-modals/withModalContext'
import { CancelConfirm } from '@eig-builder/module-modals/helpers'
import { showSuccess } from '@eig-builder/module-notification-bar-with-context/actions'
import { NotificationBarContext } from '@eig-builder/module-notification-bar-with-context'

import AddCredits from '../../../containers/account/detail-card/add-credits'
import AccountRelink from '../../../containers/account/detail-card/account-relink'
import CancelAccountDialog from '../../../containers/account/detail-card/cancel-account'
import ChangeBillingCountry from './change-billing-country'
import ChangeEmailDialog from '../../../containers/account/detail-card/change-email'
import ChangeNameDialog from './change-name-dialog'
import ConvertToTestUser from './convert-to-test-user'
import DowngradeAccountDialog from '../../../containers/account/detail-card/downgrade-account'
import RemovePaymentMethodModal from './remove-payment-method/modal'

import DowngradeAccountContextWrapper from './downgrade-account-dialog/context'
import RefundContextWrapper from '../refund/context'
import { AccountDetailsContext } from './context'

import { getCrmApiOptions } from '../../../helpers/fetch-options-helper'
import { UserHasAccessForFeature, UserHasRole, UserHasAnyRole } from '../../../helpers/role-management'
import { ROLE_FEATURES, ROLES } from '../../../helpers/role-management/role-mapping.js'
import FAAS_DECISION from '../../../enums/faas-decision'
import { loadAccountById } from '../../../store/actions'

import Text from '@eig-builder/module-localization'

const ADD_CREDITS_DIALOG = 'add-credits-dialog'
const ACCOUNT_RELINK_DIALOG = 'account-relink-dialog'
const CANCEL_ACCOUNT_DIALOG = 'cancel-account-dialog'
const CHANGE_EMAIL_DIALOG = 'change-email-dialog'
const CHANGE_NAME_DIALOG = 'change-name-dialog'
const DOWNGRADE_ACCOUNT_DIALOG = 'downgrade-account-dialog'
const REMOVE_PAYMENT_METHOD = 'remove-payment-method'
const CHANGE_BILLING_COUNTRY = 'change-billing-country'
const CONVERT_TO_TEST_USER = 'convert-to-test-user'

const NestedListItem = styled(ListItem)`
  padding-left: ${props => props.theme.spacing(8)}px !important;
`

const BottomRowMenu = ({ account, restoreAccount, handleFraudDialog, synchronizeCards }) => {
  const [{ isSuccess: changeFaasDecisionSuccess }, changeFaasDecision] = usePutAsJson()
  const [verifyReq, setVerified] = usePostAsJson()
  const { faasStatus, getFaasStatus } = useContext(AccountDetailsContext)
  const { dispatch: modalDispatch } = useModalContext()
  const { dispatch: notificationDispatch } = useContext(NotificationBarContext)
  const dispatch = useDispatch()

  const hasNoAccessToFeature = !UserHasAccessForFeature(ROLE_FEATURES.ACCOUNT_DETAILS)
  const hasNoAccessToCancelAccount = !UserHasAccessForFeature(ROLE_FEATURES.CANCEL_ACCOUNT)
  const isSinglePlatformRole = UserHasRole(ROLES.SINGLEPLATFORM)
  const isConstantContactRole = UserHasRole(ROLES.CONSTANTCONTACT)
  const isAdmin = UserHasAnyRole([ROLES.ADMIN_CST, ROLES.ADMIN_MP])

  const supportOrAdmin = UserHasAnyRole([ROLES.ADMIN_CST, ROLES.ADMIN_MP, ROLES.SUPPORT, ROLES.ESCALATED_SUPPORT, ROLES.SUPPORT_MANAGER])

  const singlePlatformCustomer = account.accountType === 2
  const constantContactCustomer = account.accountType === 3

  const [anchorEl, setAnchorEl] = useState(null)
  const [overrideMenuOpen, setOverrideMenu] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(null)

  const showConvertToTestUser = useMemo(() => {
    if (!account) {
      return false
    }

    const supportedEmailDomains = ['@endurance.com', '@appmachine.com', '@newfold.com']
    const hasNoPaymentMethod = !account.uberClientId

    return hasNoPaymentMethod && supportedEmailDomains.some(d => account.emailAdress.endsWith(d))
  }, [account])

  useEffect(() => {
    if (changeFaasDecisionSuccess) {
      showSuccess('FaaS decision has been updated.')(notificationDispatch)
      getFaasStatus(getCrmApiOptions(account, '/v1.0/fraud_prevention/faas_decision', 'text'))
    }
  }, [changeFaasDecisionSuccess])

  useEffect(() => {
    if (verifyReq.isSuccess) {
      showSuccess('The account has been verified')(notificationDispatch)
      dispatch(loadAccountById(account.partnerId, account.id))
    }
  }, [verifyReq.isSuccess])

  const toggleOverrideMenu = () => {
    setOverrideMenu(!overrideMenuOpen)
  }

  const closeAccountMenu = () => {
    setAnchorEl(null)
    setOverrideMenu(false)
  }

  const handleDialog = key => {
    setDialogOpen(key)
  }

  const closeChangeBusinessCountry = useCallback(({ refetchAccountDetails }) => {
    setDialogOpen(null)
    if (refetchAccountDetails === true) {
      dispatch(loadAccountById(account.partnerId, account.id))
    }
  }, [account])

  const handleChangeFaasDecision = decision => {
    let options = null
    if (decision === FAAS_DECISION.APPROVED) {
      options = getCrmApiOptions(account, '/v1.0/fraud_prevention/faas_decision_approve')
    } else if (decision === FAAS_DECISION.DECLINED) {
      options = getCrmApiOptions(account, '/v1.0/fraud_prevention/faas_decision_decline')
    }

    options && changeFaasDecision({}, options)
  }

  const handleVerifyAccount = () => {
    const options = getCrmApiOptions(account, '/v1.0/account/verify')
    setVerified({}, options)
  }

  const showAccountRelink = UserHasAccessForFeature(ROLE_FEATURES.ACCOUNT_RELINK)

  return (
    <>
      <IconButton
        dataElementLocation={DataElementLocations.BODY}
        dataElementLabel='cst-account-details-options-menu'
        dataElementId='cst-account-details-options-menu'
        onClick={e => setAnchorEl(e.currentTarget)}
      >
        <MoreVertIcon />
      </IconButton>
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={closeAccountMenu}>
        <List>
          {account.deleted && account.deleted.length > 0 ? (
            <ListItem
              button
              disabled={!isAdmin}
              onClick={() => {
                restoreAccount(account)
                closeAccountMenu()
              }}
            >
              <ListItemText>
                <Text message='modules.crm.components.account.restoreAccount' />
              </ListItemText>
            </ListItem>
          ) : (
            <>
              <ListItem
                button
                disabled={hasNoAccessToCancelAccount}
                onClick={() => {
                  handleDialog(CANCEL_ACCOUNT_DIALOG)
                  closeAccountMenu()
                }}
              >
                <ListItemText>
                  <Text message='modules.crm.components.account.cancelAccount.title' />
                </ListItemText>
              </ListItem>
              <ListItem
                button
                disabled={!supportOrAdmin}
                onClick={() => {
                  handleDialog(DOWNGRADE_ACCOUNT_DIALOG)
                  closeAccountMenu()
                }}
              >
                <ListItemText>
                  <Text message='modules.crm.components.account.downgradeAccount.title' />
                </ListItemText>
              </ListItem>
            </>
          )}
          {!account.isVerified &&
            <ListItem
              button
              disabled={hasNoAccessToFeature && !(isSinglePlatformRole || isConstantContactRole)}
              onClick={() => {
                closeAccountMenu()
                CancelConfirm({
                  title: 'Set account to verified',
                  content: 'Are you sure you want to set the status of the account to verified?',
                  buttonTitle: 'Confirm',
                  confirm: handleVerifyAccount
                })(modalDispatch)
              }}
            >
              <ListItemText>
                Set to Verified
              </ListItemText>
            </ListItem>
          }
          {!!account.uberClientId &&
            <ListItem
              button
              disabled={!isAdmin}
              onClick={() => {
                closeAccountMenu()
                handleDialog(CHANGE_BILLING_COUNTRY)
              }}
            >
              <ListItemText>
                Change Billing Country
              </ListItemText>
            </ListItem>
          }
          <ListItem
            button
            disabled={hasNoAccessToFeature && !(isSinglePlatformRole || isConstantContactRole)}
            onClick={() => {
              handleDialog(CHANGE_EMAIL_DIALOG)
              closeAccountMenu()
            }}
          >
            <ListItemText>
              Change Email
            </ListItemText>
          </ListItem>
          <ListItem
            button
            disabled={hasNoAccessToFeature && !(isSinglePlatformRole || isConstantContactRole)}
            onClick={() => {
              handleDialog(CHANGE_NAME_DIALOG)
              closeAccountMenu()
            }}
          >
            <ListItemText>
              Change Name
            </ListItemText>
          </ListItem>
          {showAccountRelink && account.partnerName === 'CONSTANTCONTACT' && <ListItem
            button
            disabled={false}
            onClick={() => {
              handleDialog(ACCOUNT_RELINK_DIALOG)
              closeAccountMenu()
            }}
          >
            <ListItemText>
              <Text message='modules.crm.components.account.accountRelink.title' />
            </ListItemText>
          </ListItem>}
          <ListItem
            button
            disabled={hasNoAccessToFeature || singlePlatformCustomer || constantContactCustomer}
            onClick={toggleOverrideMenu}
          >
            <ListItemText>
              <Text message='modules.crm.components.account.override' />
            </ListItemText>
            {overrideMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItem>
          <Collapse in={overrideMenuOpen}>
            <List component='div' disablePadding>
              <NestedListItem
                button
                onClick={() => {
                  handleFraudDialog('allow-domain-purchases', account, modalDispatch)
                  closeAccountMenu()
                }}
              >
                <ListItemText>
                  <Text message='modules.crm.components.account.allowDomain' />
                </ListItemText>
              </NestedListItem>
              <NestedListItem
                button
                onClick={() => {
                  handleFraudDialog('allow-mail-purchases', account, modalDispatch)
                  closeAccountMenu()
                }}
              >
                <ListItemText>
                  <Text message='modules.crm.components.account.allowMail' />
                </ListItemText>
              </NestedListItem>
              <NestedListItem
                button
                onClick={() => {
                  handleFraudDialog('allow-change-payment-method', account, modalDispatch)
                  closeAccountMenu()
                }}
              >
                <ListItemText>
                  <Text message='modules.crm.components.account.allowChangePaymentMethod' />
                </ListItemText>
              </NestedListItem>
              {faasStatus === FAAS_DECISION.DECLINED &&
                <NestedListItem
                  disabled={!isAdmin}
                  button
                  onClick={() => {
                    handleChangeFaasDecision(FAAS_DECISION.APPROVED)
                    closeAccountMenu()
                  }}
                >
                  <ListItemText>
                    FaaS: Allow new orders
                  </ListItemText>
                </NestedListItem>}
              {faasStatus === FAAS_DECISION.APPROVED &&
                <NestedListItem
                  disabled={!isAdmin}
                  button
                  onClick={() => {
                    handleChangeFaasDecision(FAAS_DECISION.DECLINED)
                    closeAccountMenu()
                  }}
                >
                  <ListItemText>
                    FaaS: Block new orders
                  </ListItemText>
                </NestedListItem>}
            </List>
          </Collapse>
          {account.uberClientId && (
            <>
              <ListItem
                button
                disabled={hasNoAccessToFeature || singlePlatformCustomer || constantContactCustomer}
                onClick={() => {
                  synchronizeCards(account)
                  closeAccountMenu()
                }}
              >
                <ListItemText>
                  <Text message='modules.crm.components.account.synchronizeCards' />
                </ListItemText>
              </ListItem>
              <ListItem
                button
                disabled={hasNoAccessToFeature || singlePlatformCustomer || constantContactCustomer}
                onClick={() => {
                  closeAccountMenu()
                  handleDialog(REMOVE_PAYMENT_METHOD)
                }}
              >
                <ListItemText>
                  Remove Payment Method
                </ListItemText>
              </ListItem>
            </>
          )}
          <ListItem
            button
            disabled={!supportOrAdmin}
            onClick={() => {
              handleDialog(ADD_CREDITS_DIALOG)
              closeAccountMenu()
            }}
          >
            <ListItemText>
              <Text message='modules.crm.components.account.addCredits.title' />
            </ListItemText>
          </ListItem>
          {showConvertToTestUser &&
            <ListItem
              button
              disabled={!supportOrAdmin}
              onClick={() => {
                handleDialog(CONVERT_TO_TEST_USER)
                closeAccountMenu()
              }}
            >
              Convert to test user
            </ListItem>}
        </List>
      </Popover>
      <AddCredits open={dialogOpen === ADD_CREDITS_DIALOG} onClose={handleDialog} />
      <AccountRelink open={dialogOpen === ACCOUNT_RELINK_DIALOG} onClose={handleDialog} />
      <ChangeEmailDialog open={dialogOpen === CHANGE_EMAIL_DIALOG} handleDialog={handleDialog} />
      <ChangeNameDialog open={dialogOpen === CHANGE_NAME_DIALOG} handleDialog={handleDialog} />
      {dialogOpen === REMOVE_PAYMENT_METHOD && <RemovePaymentMethodModal account={account} handleClose={handleDialog} />}
      {dialogOpen === CHANGE_BILLING_COUNTRY && <ChangeBillingCountry account={account} handleClose={closeChangeBusinessCountry} />}
      {dialogOpen === CONVERT_TO_TEST_USER && <ConvertToTestUser account={account} handleClose={handleDialog} />}
      <RefundContextWrapper>
        {dialogOpen === CANCEL_ACCOUNT_DIALOG && <CancelAccountDialog handleDialog={handleDialog} />}
        <DowngradeAccountContextWrapper>
          {dialogOpen === DOWNGRADE_ACCOUNT_DIALOG && <DowngradeAccountDialog handleDialog={handleDialog} />}
        </DowngradeAccountContextWrapper>
      </RefundContextWrapper>
    </>
  )
}

BottomRowMenu.propTypes = {
  account: PropTypes.object.isRequired,
  restoreAccount: PropTypes.func.isRequired,
  handleFraudDialog: PropTypes.func.isRequired,
  synchronizeCards: PropTypes.func.isRequired
}

export default BottomRowMenu
